import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';
import {
  AssetCategory,
  AssetStatus,
  AssetType,
  CampaignAsset,
} from '../types';

export interface AssetListFilters {
  campaign?: string;
  brand?: string;
  category?: AssetCategory;
  type?: AssetType;
  status?: AssetStatus;
  from?: string;
  to?: string;
  /** `true` → only the "to plan" tray; `false` → only dated assets. */
  unscheduled?: boolean;
}

const listKey = (f?: AssetListFilters) => ['assets', f ?? {}] as const;
const oneKey = (id: number) => ['asset', id] as const;

const buildQuery = (f?: AssetListFilters): string => {
  if (!f) return '';
  const params = new URLSearchParams();
  if (f.campaign) params.set('campaign', f.campaign);
  if (f.brand) params.set('brand', f.brand);
  if (f.category) params.set('category', f.category);
  if (f.type) params.set('type', f.type);
  if (f.status) params.set('status', f.status);
  if (f.from) params.set('from', f.from);
  if (f.to) params.set('to', f.to);
  if (f.unscheduled === true) params.set('unscheduled', 'true');
  else if (f.unscheduled === false) params.set('unscheduled', 'false');
  const s = params.toString();
  return s ? `?${s}` : '';
};

export const listAssets = async (f?: AssetListFilters): Promise<CampaignAsset[]> => {
  const res = await axios.get(`api/assets/${buildQuery(f)}`);
  return res.data;
};

const getAsset = async (id: number): Promise<CampaignAsset> => {
  const res = await axios.get(`api/assets/${id}/`);
  return res.data;
};

export const useAssets = (f?: AssetListFilters) =>
  useQuery({ queryKey: listKey(f), queryFn: () => listAssets(f) });

export const useAsset = (id: number | undefined) =>
  useQuery({
    queryKey: oneKey(id ?? -1),
    queryFn: () => getAsset(id as number),
    enabled: !!id,
  });

export interface CreateAssetInput {
  title: string;
  category: AssetCategory;
  asset_type: AssetType;
  /** Optional — set this OR `brands` (or both). */
  campaign?: string | null;
  /** Optional direct-brand link for standalone assets (one-off post tied to
   *  a brand without a campaign). */
  brands?: string[];
  /** Email-only — id of the brand this newsletter is dedicated to.
   *  Lets it count as 'Dedicated newsletter' on that brand's visibility plan. */
  dedicated_brand?: string | null;
  /** Optional — when null/empty, the asset lands in the "To plan" tray. */
  publish_date?: string | null;
  end_date?: string | null;
  publish_time?: string | null;
  brand_spots?: number | null;
  slot_price?: string | null;
  sold_as?: number | null;
  status?: AssetStatus;
  description?: string;
  caption?: string;
  theme?: string;
  segment?: string;
  proof_url?: string;
  proof_screenshot?: string;
  proof_extra_screenshots?: string[];
}

export const useCreateAsset = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateAssetInput): Promise<CampaignAsset> => {
      const res = await axios.post('api/assets/', payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useUpdateAsset = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: { id: number; patch: Partial<CampaignAsset> }): Promise<CampaignAsset> => {
      const res = await axios.patch(`api/assets/${id}/`, patch);
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      // A new date moves the placement's linked tasks too (backend).
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.setQueryData(oneKey(data.id), data);
    },
  });
};

export const useDeleteAsset = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await axios.delete(`api/assets/${id}/`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

/** Measured on-site numbers for one placement, read by the backend from perfy. */
export interface PlacementMetricsWeek {
  week: string;
  week_start: string;
  placement_id: string;
  placement_type: string;
  placement_brand: string;
  views: number;
  clicks: number;
  ctr: number | null;
}
export interface PageReachWeek {
  week: string;
  week_start: string;
  page_type: string;
  page_path: string;
  sessions: number;
  pageviews: number;
}
export interface PlacementMetrics {
  placement_id: string;
  configured: boolean;
  start_date: string;
  end_date: string;
  daily: Array<{ date: string; views: number; clicks: number; placement_type: string; page_type: string }>;
  weekly: PlacementMetricsWeek[];
  page_reach_weekly: PageReachWeek[];
  freshness: {
    placements_through: string | null;
    reach_through: string | null;
    synced_at: string | null;
    last_sync_status: string | null;
  } | null;
  error: string | null;
}

export const useAssetMetrics = (id: number | undefined) =>
  useQuery({
    queryKey: ['asset-metrics', id ?? -1] as const,
    queryFn: async (): Promise<PlacementMetrics> => {
      const res = await axios.get(`api/assets/${id}/metrics/`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 60 * 60 * 1000,
  });

export interface SiteMetricsTotal {
  views: number;
  clicks: number;
  ctr: number | null;
  start_date: string;
  end_date: string;
}

/** Measured views/clicks on lamptwist.com for several placements, summed over
 *  the days each one was live (lookbook). Keyed by placement id. */
export const useSiteMetrics = (ids: number[]) => {
  const key = [...ids].sort((a, b) => a - b).join(',');
  return useQuery({
    queryKey: ['site-metrics', key] as const,
    queryFn: async (): Promise<Record<string, SiteMetricsTotal>> => {
      const res = await axios.get('api/assets/site-metrics/', { params: { ids: key } });
      return res.data.placements ?? {};
    },
    enabled: key.length > 0,
    staleTime: 60 * 60 * 1000,
  });
};

export interface KlaviyoCampaign {
  id: string;
  name: string;
  status: string;
  send_time: string | null;
}

/** Sent Klaviyo newsletters for the picker on a newsletter placement. */
export const useKlaviyoCampaigns = (q: string, enabled: boolean) =>
  useQuery({
    queryKey: ['klaviyo-campaigns', q] as const,
    queryFn: async (): Promise<KlaviyoCampaign[]> =>
      (await axios.get('api/klaviyo/campaigns/', { params: { q } })).data,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

/** Link (or refresh) / unlink a newsletter placement and its Klaviyo campaign.
 *  Doesn't refresh the placement lists itself: the open placement form would
 *  reset on a new `asset` prop. KlaviyoLink does it when the form closes. */
export const useKlaviyoLink = () =>
  useMutation({
    mutationFn: async ({ id, campaignId }: { id: number; campaignId: string | null }) =>
      (campaignId
        ? await axios.post(`api/assets/${id}/klaviyo/`, { campaign_id: campaignId })
        : await axios.delete(`api/assets/${id}/klaviyo/`)
      ).data as CampaignAsset & { warning?: string | null },
  });

export interface InstagramMedia {
  id: string;
  kind: 'Post' | 'Carousel' | 'Reel' | 'Story';
  caption: string;
  timestamp: string | null;
  permalink: string;
  thumbnail: string;
  /** A story Instagram no longer serves, kept by mosy: picture via
   *  useInstagramStoryPicture. */
  saved_story: boolean;
}

/** LampTwist's recent Instagram posts, reels and stories for the picker. */
export const useInstagramMedia = (enabled: boolean) =>
  useQuery({
    queryKey: ['instagram-media'] as const,
    queryFn: async (): Promise<InstagramMedia[]> => (await axios.get('api/instagram/media/')).data,
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

export const useInstagramStoryPicture = (mediaId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['instagram-story-picture', mediaId] as const,
    queryFn: async (): Promise<string> =>
      (await axios.get(`api/instagram/stories/${encodeURIComponent(mediaId)}/picture/`)).data.picture,
    enabled,
    staleTime: Infinity,
  });

/** Link (or refresh) / unlink an Instagram placement and its post. Like
 *  useKlaviyoLink, leaves refreshing the lists to the picker on close. */
export const useInstagramLink = () =>
  useMutation({
    mutationFn: async ({ id, mediaId }: { id: number; mediaId: string | null }) =>
      (mediaId
        ? await axios.post(`api/assets/${id}/instagram/`, { media_id: mediaId })
        : await axios.delete(`api/assets/${id}/instagram/`)
      ).data as CampaignAsset,
  });

/** Brand slots and/or "Sold as" on several placements at once. Leave a key
 *  out to keep each placement's own value; sold_as null = automatic. */
export const useBulkSlots = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { ids: number[]; brand_spots?: number | null; sold_as?: number | null }) =>
      (await axios.post('api/assets/bulk-slots/', body)).data as { updated: number },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['partnership'] });
    },
  });
};
